// GRN phase 1, per the approved mockup (2026-10-22): after the lines are ticked, Create Purchase Order shows the
// chosen supplier's lines and asks "How was it bought?" on the page (laptops; phones keep the bottom sheet).
// Nothing is pre-selected (g02). Order from vendor: expected delivery (g11) + notes, then Create PO (Ordered).
// Already bought: Continue opens the direct purchase page.
import React, { useState } from 'react';
import { Button, Card, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Dayjs } from 'dayjs';
import { PathPicker, PurchasePath } from '../../components/receiving/PathChoice';
import { money } from '../../components/receiving/shared';
import type { ApprovableLine } from './POCreate';

const { Title } = Typography;
const MUTED = '#5f6672';
const fieldLabel: React.CSSProperties = { fontWeight: 600, display: 'block', marginBottom: 6 };

interface Props {
  lines: ApprovableLine[];
  renderItem: (l: ApprovableLine) => React.ReactNode;
  /** Estimate shown per line: the MR line price, or one typed on Create PO. */
  priceOf: (l: ApprovableLine) => number;
  notes: string;
  setNotes: (v: string) => void;
  busy: boolean;
  onCancel: () => void;
  onDirect: () => void;
  onOrder: (expectedDelivery: string) => void;
}

const POCreatePathStep: React.FC<Props> = ({ lines, renderItem, priceOf, notes, setNotes, busy, onCancel, onDirect, onOrder }) => {
  const [path, setPath] = useState<PurchasePath | null>(null);
  const [date, setDate] = useState<Dayjs | null>(null);
  const vendor = lines[0]?.vendor || null;
  const mrIds = Array.from(new Set(lines.map((l) => l.mrHeader?.mrId).filter(Boolean)));
  const departments = Array.from(new Set(lines.map((l) => l.mrHeader?.department ? `${l.mrHeader.department.name} (${l.mrHeader.department.code})` : '').filter(Boolean)));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={onCancel}>Back</Button>
          <Title level={4} style={{ margin: 0 }}>Create Purchase Order</Title>
        </Space>
        <Tag style={{ margin: 0, fontSize: 13, padding: '2px 10px' }}>
          {lines.length} line{lines.length === 1 ? '' : 's'} selected · {mrIds.join(', ')}
        </Tag>
      </div>
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16, marginBottom: 18 }}>
          <div>
            <label htmlFor="po-vendor" style={fieldLabel}><span style={{ color: '#cf1322' }}>*</span> Vendor</label>
            {/* The supplier comes from the ticked lines (one supplier per PO); changing it is done per line with Refine. */}
            <Select id="po-vendor" value={vendor?.id} options={vendor ? [{ value: vendor.id, label: vendor.name }] : []} style={{ width: '100%' }} />
          </div>
          <div>
            <label htmlFor="po-dept" style={fieldLabel}>Department</label>
            <Input id="po-dept" value={departments.join(', ')} disabled />
          </div>
        </div>
        <span style={fieldLabel}><span style={{ color: '#cf1322' }}>*</span> How was it bought?</span>
        <PathPicker
          vendorId={vendor?.id ?? null}
          vendorName={vendor?.name || ''}
          path={path}
          onPath={setPath}
          date={date}
          onDate={setDate}
          layout="page"
          notes={notes}
          onNotes={setNotes}
        />
        <Table<ApprovableLine>
          style={{ marginTop: 18 }}
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={lines}
          columns={[
            { title: '#', key: 'n', width: 44, render: (_: any, __: ApprovableLine, i: number) => i + 1 },
            { title: 'Item', key: 'item', render: (_: any, l: ApprovableLine) => renderItem(l) },
            {
              title: 'For', key: 'for', width: 220,
              render: (_: any, l: ApprovableLine) => l.costCenter
                ? <span><code>{l.costCenter.code}</code><div style={{ fontSize: 12, color: MUTED }}>{l.costCenter.name}</div></span>
                : <span style={{ color: MUTED }}>—</span>,
            },
            { title: 'Qty', key: 'qty', width: 80, align: 'right' as const, render: (_: any, l: ApprovableLine) => Number((l as any).reorderQty) > 0 ? Number((l as any).reorderQty) : Number(l.qty) },
            { title: 'Unit', key: 'unit', width: 80, render: (_: any, l: ApprovableLine) => l.unit?.name || '' },
            { title: 'Est. price', key: 'price', width: 110, align: 'right' as const, render: (_: any, l: ApprovableLine) => money(priceOf(l)) },
          ]}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            loading={busy}
            disabled={!path || (path === 'order' && !date)}
            onClick={() => (path === 'direct' ? onDirect() : onOrder(date!.format('YYYY-MM-DD')))}
          >
            {path === 'order' ? 'Create PO (Ordered)' : 'Continue'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default POCreatePathStep;
